import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { FilePreviewModal } from '@/components/ui/FilePreviewModal';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ApiRequestError } from '@/lib/apiClient';
import type {
  ActivityListParams,
  ActivityStatus,
  ActivityType,
  ClassActivity,
} from '@/types/activity';
import { activitiesApi } from '../api/activities.api';
import { ACTIVITY_STATUS_LABEL, ACTIVITY_TYPE_LABEL } from '../activities/constants';
import { describeScore } from '../activities/utils';
import { instituteToday } from '../utils';
import type { TaughtClass } from '../types/teacher';
import {
  ActivityFilterBar,
  ActivityHeader,
  ActivityQuickActionsWidget,
  ActivitySummaryWidget,
  ActivityTable,
  ACTIVITY_STATE_PILL,
  CreateActivityForm,
  UpcomingActivitiesWidget,
  activityState,
  type TabKey,
} from './activities';

interface TeacherActivitiesSectionProps {
  currentClass: TaughtClass;
  /** Passed down rather than fetched again — the dashboard already has them. */
  allocatedClasses: TaughtClass[];
  selectedClassId?: string;
  onSelectClass?: (id: string) => void;
}

/**
 * The list's filters, as they sit in the address bar, so reviewing an activity
 * and coming back finds the same class, tab, search and page.
 *
 * `classSectionUuid` is empty until the teacher picks a class in the header —
 * the console's Active Class stands in until then. Not `class`: the dashboard
 * around this section reads that name as an allotment id. `view` is the tab
 * strip, likewise kept off the dashboard's own `tab`.
 */
type Filters = {
  classSectionUuid: string;
  activityType: ActivityType | '';
  dueDate: string;
  status: ActivityStatus | '';
  view: TabKey;
  search: string;
  page: number;
};

const DEFAULT_FILTERS: Filters = {
  classSectionUuid: '',
  activityType: '',
  dueDate: '',
  status: '',
  view: 'all',
  search: '',
  page: 1,
};

/** The class is a uuid the server scopes; only the fixed sets are checked here. */
const FILTER_OPTIONS = {
  allowed: {
    activityType: Object.keys(ACTIVITY_TYPE_LABEL),
    status: Object.keys(ACTIVITY_STATUS_LABEL),
    // Every state an activity can be in; `all`, the default, needs no entry.
    view: Object.keys(ACTIVITY_STATE_PILL),
  },
};

export function TeacherActivitiesSection({
  currentClass,
  allocatedClasses,
  selectedClassId,
  onSelectClass,
}: TeacherActivitiesSectionProps) {
  const navigate = useNavigate();
  // The institute's date, not the device's UTC one: before 05:30 IST the two
  // differ, and an activity due today would read as completed already.
  const today = instituteToday();

  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const { view: activeTab, search: searchQuery, page } = filters;
  /** What the filter bar edits and the list request carries. */
  const listFilters: ActivityListParams = {
    classSectionUuid: filters.classSectionUuid,
    activityType: filters.activityType,
    dueDate: filters.dueDate,
    status: filters.status,
  };

  // Any change to what is listed starts the reader on page 1 again.
  function updateFilters(patch: Partial<Filters>) {
    setFilters({ ...patch, page: patch.page ?? 1 });
  }

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  /** The activity being corrected, when the form is open to edit rather than to set. */
  const [editing, setEditing] = useState<ClassActivity | null>(null);
  const [deleting, setDeleting] = useState<ClassActivity | null>(null);
  /** The activity whose guidelines sheet is open in the preview. */
  const [previewing, setPreviewing] = useState<ClassActivity | null>(null);

  // The form sits above the table; scroll to the top so the heading and form appear.
  useEffect(() => {
    if (isFormOpen || editing) {
      const timer = setTimeout(() => {
        const headerEl = document.getElementById('activity-page-header');
        if (headerEl) {
          headerEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [isFormOpen, editing]);

  const [isDeleting, setIsDeleting] = useState(false);

  const [pageSize, setPageSize] = useState(10);

  // Active class selector options — one class at a time, never all of them.
  const classSelectOptions = useMemo(
    () =>
      allocatedClasses.map((c) => ({
        value: c.classSectionUuid,
        // A class-teacher-only allotment has no subject; leaving the part out
        // beats printing "— null".
        label: c.subject
          ? `${c.className} — ${c.subject} (${c.role})`
          : `${c.className} (${c.role})`,
      })),
    [allocatedClasses],
  );

  /** The class picked here, when it is still one of theirs — an old link's may not be. */
  const pickedClass = allocatedClasses.find(
    (c) => c.classSectionUuid === filters.classSectionUuid,
  );

  const activeSectionUuid = useMemo(() => {
    if (pickedClass) return pickedClass.classSectionUuid;
    if (selectedClassId) {
      const match = allocatedClasses.find((c) => c.id === selectedClassId);
      if (match) return match.classSectionUuid;
    }
    return currentClass.classSectionUuid;
  }, [pickedClass, selectedClassId, allocatedClasses, currentClass.classSectionUuid]);

  /**
   * A class restored from the address is handed to the console, as picking it
   * does, so the top bar and the new-activity form follow the list. Once only:
   * after that the two move together through `handleClassChange`. Repeated
   * until the console has taken it, because the console fills in its own first
   * class in the same render the allotments arrive, and that would win.
   */
  const classHandedOver = useRef(false);
  useEffect(() => {
    if (classHandedOver.current || allocatedClasses.length === 0) return;
    if (
      !pickedClass ||
      !onSelectClass ||
      pickedClass.classSectionUuid === currentClass.classSectionUuid
    ) {
      classHandedOver.current = true;
      return;
    }
    onSelectClass(pickedClass.id);
  }, [
    allocatedClasses.length,
    pickedClass,
    onSelectClass,
    currentClass.classSectionUuid,
    selectedClassId,
  ]);

  // Listed for the class the selector shows, so the list and the dropdown can
  // never disagree. No class yet (allotments still loading) means nothing to ask.
  const { data, isLoading, error, refetch } = useApiQuery(
    () =>
      activeSectionUuid
        ? activitiesApi.list({ ...listFilters, classSectionUuid: activeSectionUuid })
        : Promise.resolve([]),
    [activeSectionUuid, filters.activityType, filters.dueDate, filters.status],
  );

  const activities = data ?? [];

  // Withdrawn activities are a separate read — the server leaves them out of the
  // live list — fetched so counts and cancelled tab stay in sync.
  const isCancelledTab = activeTab === 'cancelled';
  const withdrawn = useApiQuery(
    () =>
      activeSectionUuid
        ? activitiesApi.list({ ...listFilters, classSectionUuid: activeSectionUuid, withdrawn: true })
        : Promise.resolve([]),
    [activeSectionUuid, filters.activityType, filters.dueDate, filters.status],
  );
  const listed = isCancelledTab ? (withdrawn.data ?? []) : activities;
  const listLoading = isCancelledTab ? withdrawn.isLoading : isLoading;
  const listError = isCancelledTab ? withdrawn.error : error;
  const listRefetch = isCancelledTab ? withdrawn.refetch : refetch;

  function handleClassChange(val: string) {
    updateFilters({ classSectionUuid: val, dueDate: '' });
    if (onSelectClass && val) {
      const matched = allocatedClasses.find((ac) => ac.classSectionUuid === val);
      if (matched) onSelectClass(matched.id);
    }
  }

  async function confirmDelete() {
    if (!deleting) return;
    setIsDeleting(true);

    try {
      await activitiesApi.remove(deleting.uuid);
      toast.success(
        deleting.publication === 'DRAFT'
          ? `The draft ${deleting.title} has been deleted`
          : `${deleting.title} has been withdrawn`,
        deleting.publication === 'DRAFT'
          ? 'The class never saw it.'
          : 'The marks recorded against it are kept.',
      );
      setDeleting(null);
      refetch();
      // So the Cancelled tab shows it the next time it is opened.
      withdrawn.refetch();
    } catch (caught) {
      setDeleting(null);
      toast.error(
        `Could not withdraw ${deleting.title}`,
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
    } finally {
      setIsDeleting(false);
    }
  }

  /** Releases a draft as it stands, from the row's menu. */
  async function publishDraft(activity: ClassActivity) {
    // The server refuses this too; caught here so the teacher is sent to the
    // field that needs changing rather than shown a bare refusal.
    if (activity.dueDate < today) {
      toast.error('The due date has passed', 'Edit the draft, choose a new due date, then publish.');
      return;
    }
    try {
      const published = await activitiesApi.update(activity.uuid, { publication: 'PUBLISHED' });
      toast.success(
        `${published.title} is set`,
        `${published.classSection.displayName} · ${published.subject.name}`,
      );
      refetch();
    } catch (caught) {
      toast.error(
        `Could not publish ${activity.title}`,
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
    }
  }

  // Filter activities by tab, search query, date
  const filteredActivities = useMemo(() => {
    let result = listed;

    // 1. Tab filter — the same rule as the row labels and the summary tiles.
    // Cancelled is already its own read, so only the live tabs narrow here.
    if (activeTab !== 'all' && activeTab !== 'cancelled') {
      result = result.filter((a) => activityState(a, today) === activeTab);
    }

    // 2. Search query filter
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.description.toLowerCase().includes(q) ||
          (a.instructions && a.instructions.toLowerCase().includes(q)) ||
          a.subject.name.toLowerCase().includes(q) ||
          a.classSection.displayName.toLowerCase().includes(q) ||
          ACTIVITY_TYPE_LABEL[a.activityType].toLowerCase().includes(q),
      );
    }

    return result;
  }, [listed, activeTab, searchQuery, today]);

  // Pagination
  const totalPages = Math.max(Math.ceil(filteredActivities.length / pageSize), 1);
  const paginatedActivities = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredActivities.slice(start, start + pageSize);
  }, [filteredActivities, page, pageSize]);

  // Tab counts for all tabs
  const tabCounts = useMemo<Record<TabKey, number>>(() => {
    let upcoming = 0;
    let ongoing = 0;
    let completed = 0;
    let draft = 0;

    activities.forEach((a) => {
      const state = activityState(a, today);
      if (state === 'upcoming') upcoming += 1;
      else if (state === 'ongoing') ongoing += 1;
      else if (state === 'completed') completed += 1;
      else if (state === 'draft') draft += 1;
    });

    return {
      all: activities.length,
      upcoming,
      ongoing,
      completed,
      draft,
      cancelled: withdrawn.data?.length ?? 0,
    };
  }, [activities, withdrawn.data, today]);

  // Summary statistics for 2x2 widget. Drafts stay out of every tile: the
  // class has not been given them, so they are nobody's work yet.
  const summaryStats = useMemo(() => {
    let total = 0;
    let upcoming = 0;
    let ongoing = 0;
    let completed = 0;

    // Each live activity is in exactly one state, so the three add up to the total.
    activities.forEach((a) => {
      const state = activityState(a, today);
      if (state !== 'draft') total += 1;
      if (state === 'upcoming') upcoming += 1;
      else if (state === 'ongoing') ongoing += 1;
      else if (state === 'completed') completed += 1;
    });

    return { total, upcoming, ongoing, completed };
  }, [activities, today]);

  // Upcoming activities list for widget (sorted ascending by dueDate). Due
  // today or later only: an overdue activity still left open is not upcoming.
  const upcomingWidgetList = useMemo(() => {
    return [...activities]
      .filter((a) => a.publication === 'PUBLISHED' && a.dueDate >= today)
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
      .slice(0, 4);
  }, [activities, today]);

  // The class is a selection, not a filter: there is always one, so it never
  // counts towards "filtered" or makes "Clear all" appear.
  const hasExtraFilters = Boolean(filters.activityType || filters.dueDate || filters.status);

  return (
    <div className="flex flex-col gap-6">
      {/* 1. Header & Active Class Selector */}
      <ActivityHeader
        activeSectionUuid={activeSectionUuid}
        classSelectOptions={classSelectOptions}
        onClassChange={handleClassChange}
        isFormOpen={isFormOpen}
        onToggleForm={() => {
          // Closing mid-edit drops the edit, so the button next opens a blank form.
          setEditing(null);
          setIsFormOpen((open) => !open);
        }}
      />

      {/* 2. Create / Assign Activity Form — or the same form, editing one */}
      {isFormOpen && (
        <CreateActivityForm
          key={editing?.uuid ?? 'new'}
          currentClass={currentClass}
          editing={editing}
          onSuccess={() => {
            setEditing(null);
            setIsFormOpen(false);
            refetch();
          }}
          onCancel={() => {
            setEditing(null);
            setIsFormOpen(false);
          }}
        />
      )}

      {/* 3. Main Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column (8-cols): Tabs, Search, Activities Table & Pagination */}
        <div className="lg:col-span-8 flex flex-col gap-4 min-w-0">
          <ActivityFilterBar
            activeTab={activeTab}
            onSelectTab={(tab) => updateFilters({ view: tab })}
            searchQuery={searchQuery}
            onSearchChange={(query) => updateFilters({ search: query })}
            filters={listFilters}
            onFilterChange={(newFilters) =>
              updateFilters({
                activityType: newFilters.activityType ?? '',
                dueDate: newFilters.dueDate ?? '',
                status: newFilters.status ?? '',
              })
            }
            isFilterPanelOpen={isFilterPanelOpen}
            onToggleFilterPanel={() => setIsFilterPanelOpen((open) => !open)}
            tabCounts={tabCounts}
          />

          <ActivityTable
            isLoading={listLoading}
            error={listError}
            activities={paginatedActivities}
            totalCount={filteredActivities.length}
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={(next) => setFilters({ page: next })}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setFilters({ page: 1 });
            }}
            searchQuery={searchQuery}
            hasExtraFilters={hasExtraFilters}
            activeTab={activeTab}
            today={today}
            onRetry={listRefetch}
            onReview={(act) => navigate(`/teacher/activities/${act.uuid}/review`)}
            onEdit={(act) => {
              setEditing(act);
              setIsFormOpen(true);
            }}
            onViewGuideline={setPreviewing}
            onPublish={(act) => void publishDraft(act)}
            onDelete={setDeleting}
          />
        </div>

        {/* Right Column (4-cols): Summary, Upcoming & Quick Actions */}
        <div className="lg:col-span-4 flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <ActivitySummaryWidget summaryStats={summaryStats} />
            {/* The tiles count the list as filtered, so they say so rather
                than passing a filtered count off as the class's total. */}
            {hasExtraFilters && (
              <p className="text-xs text-ink-3 font-medium px-1">
                Showing filtered activities — the summary counts only those matching your filters.
              </p>
            )}
          </div>

          <UpcomingActivitiesWidget
            upcomingActivities={upcomingWidgetList}
            onSelectActivity={(item) => navigate(`/teacher/activities/${item.uuid}/review`)}
            onViewAll={() => {
              updateFilters({ view: 'upcoming' });
              setTimeout(() => {
                const targetEl =
                  document.getElementById('activity-filter-bar') ||
                  document.getElementById('activity-page-header');
                if (targetEl) {
                  targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } else {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }, 60);
            }}
          />

          <ActivityQuickActionsWidget
            onCreateClick={() => {
              setEditing(null);
              setIsFormOpen(true);
              setTimeout(() => {
                const headerEl = document.getElementById('activity-page-header');
                if (headerEl) {
                  headerEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } else {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }, 60);
            }}
            onCategoriesClick={() => {
              setIsFilterPanelOpen(true);
              setTimeout(() => {
                const filterEl = document.getElementById('activity-filter-bar');
                if (filterEl) {
                  filterEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } else {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }, 60);
            }}
            onReportsClick={() => {
              // The newest one the class has been set, as the server orders the
              // list. A draft has nobody's work to report on yet.
              const latest = activities.find((a) => activityState(a, today) !== 'draft');
              if (latest) {
                navigate(`/teacher/activities/${latest.uuid}/review`);
              } else {
                toast.info('No activities yet', 'Set an activity for this class to see its report.');
              }
            }}
          />
        </div>
      </div>

      <FilePreviewModal
        source={previewing?.guideline ? { path: activitiesApi.guidelinePath(previewing.uuid) } : null}
        fileName={previewing?.guideline?.name ?? 'Guidelines'}
        onClose={() => setPreviewing(null)}
      />

      {/* Delete / Suspend Confirmation Dialog */}
      <ConfirmDialog
        isOpen={Boolean(deleting)}
        tone="danger"
        title={
          deleting?.publication === 'DRAFT'
            ? `Delete the draft ${deleting.title}?`
            : `Suspend ${deleting?.title ?? 'this activity'}?`
        }
        description={
          deleting?.publication === 'DRAFT'
            ? 'The class never saw it. It moves to the Cancelled tab.'
            : deleting
              ? `Nothing is deleted. It stops appearing for ${deleting.classSection.displayName}, and the ${describeScore(null, deleting.maxPoints) === 'Not scored' ? 'record' : 'marks'} already recorded against it are kept.`
              : undefined
        }
        confirmLabel={deleting?.publication === 'DRAFT' ? 'Delete draft' : 'Suspend activity'}
        isLoading={isDeleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}
